import {ChangeDetectionStrategy, Component, Inject, OnDestroy} from '@angular/core';
import dayjs from "dayjs";
import {SharedService} from "@shared/shared.service";
import {take} from "rxjs/operators";
import {BehaviorSubject, catchError, EMPTY, Observable, Subject} from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import {TranslateService} from "@ngx-translate/core";
import {BillingContractModel} from "@services/back-office/model/billingContractModel";
import {BillingService} from "@shared/services/billing.service";
import {ToastrService} from "ngx-toastr";
import {BillingDocumentRequest} from "@services/back-office/model/billingDocumentRequest";
import {BillingActServiceModel} from "@services/back-office/model/billingActServiceModel";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UntypedFormControl} from "@angular/forms";

/** «Додати рахунок» / «Додати акт» of the contract card: the same request, priced from the contract or from service lines. */
@Component({
    selector: 'app-add-document-dialog',
    templateUrl: './add-document-dialog.component.html',
    styleUrls: ['./add-document-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddDocumentDialogComponent implements OnDestroy {
    private destroyed$ = new Subject<void>();
    loading$ = new BehaviorSubject(false);
    minDate = new Date();

    send: UntypedFormControl = new UntypedFormControl(false);
    /** «З ПДВ» of a Dah Development act; the backend applies the date rule when it is not sent. */
    vat: UntypedFormControl = new UntypedFormControl(true);
    /** The month the document is for; the months picker hands over a Date. */
    period: Date | null = null;
    date: Date | string = '';

    document: BillingDocumentRequest = {
        number: '',
        comment: ''
    };

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            contract: BillingContractModel,
            documentType: 'INVOICE' | 'ACT'
        },
        public dialogRef: MatDialogRef<AddDocumentDialogComponent>,
        private sharedService: SharedService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private billingService: BillingService,
    ) {
        if (this.data.contract.data && this.data.contract.data.length) {
            const firstDate = this.data.contract.data[this.data.contract.data.length - 1].date;
            if (firstDate) {
                this.minDate = dayjs(firstDate, 'YYYY-MM-DD').toDate();
            }
        }
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.loading$.complete();
    }

    isInvoice(): boolean {
        return this.data.documentType == 'INVOICE';
    }

    canChooseVat(): boolean {
        return !this.isInvoice() && !!this.data.contract.vatPayer;
    }

    title(): string {
        return this.isInvoice() ? 'Додати рахунок' : 'Додати акт';
    }

    submitForm() {
        if (!this.period) {
            this.toastrService.warning('Оберіть період');
            return;
        }

        const services = this.document.services ? this.document.services.filter(item => !!item) : [];
        for (const service of services) {
            if (!service.name || !service.qty || !service.unit || !service.price) {
                this.toastrService.warning(`Заповніть усі обов'язкові поля`);
                return;
            }
        }

        this.loading$.next(true);

        const request: BillingDocumentRequest = {
            periodFrom: dayjs(this.period).startOf('month').format('YYYY-MM-DD'),
            ...this.date && {date: dayjs(this.date).format('YYYY-MM-DDTHH:mm:ss')},
            ...this.document.number && {number: this.document.number},
            ...this.document.comment && {comment: this.document.comment},
            ...services.length > 0 && {services, custom: true},
            ...this.isInvoice() && {send: this.send.value},
            ...this.canChooseVat() && {vat: this.vat.value}
        };

        const request$: Observable<unknown> = this.isInvoice()
            ? this.billingService.addInvoice(this.data.contract.id!, request)
            : this.billingService.addAct(this.data.contract.id!, request);

        request$.pipe(
            take(1),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe(() => {
            this.loading$.next(false);
            this.closeDialog(true);
        });
    }

    countAmount(qty?: number, price?: number) {
        if (qty && price) {
            return Number((qty * price).toFixed(2));
        }

        return 0;
    }

    getTotal() {
        const services = this.document.services ? this.document.services : [];
        return services.reduce((total, service) => total + this.countAmount(service?.qty, service?.price), 0);
    }

    addRow() {
        const emptyService: BillingActServiceModel = {
            name: '',
            qty: 0,
            price: 0,
            unit: ''
        };

        this.document = {
            ...this.document,
            services: this.document.services ? [...this.document.services, emptyService] : [emptyService]
        };
    }

    deleteRow(key: number) {
        if (this.document.services) {
            this.document = {...this.document, services: this.document.services.filter((item, index) => index != key)};
        }
    }

    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }
}
